import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { AI_GRADIENT_STOPS } from 'common/constants/ai';
import AISuggestionPopover from 'pages/Documents/components/SuggestedDocumentReference/AISuggestionPopover';
import { AISuggestionChipProps } from './AISuggestionChip.props';
import {
  acceptedChipContainerSx,
  acceptedRectStyle,
  chipContainerSx,
  commentaryItemSx,
  commentaryListSx,
  declinedChipContainerSx,
  declinedLabelTypographySx,
  labelTypographySx,
  rectStyle,
  svgStyle,
} from './AISuggestionChip.styles';

const AISuggestionChip = ({
  label,
  value,
  commentary,
  confidence,
  onAccept,
  onDecline,
  isAccepted,
  isDeclined,
}: AISuggestionChipProps) => {
  const gradientId = `ai-gradient-chip-${value}`;

  const { popoverTitle, popoverSubtitle } = useMemo(() => {
    if (isAccepted) {
      return { popoverTitle: 'AI Suggested Mapping (Accepted)', popoverSubtitle: 'This suggestion has been accepted' };
    }
    if (isDeclined) {
      return { popoverTitle: 'AI Suggested Mapping (Declined)', popoverSubtitle: 'This suggestion was declined' };
    }
    return { popoverTitle: 'AI Suggested Mapping', popoverSubtitle: 'Review and accept this suggestion' };
  }, [isAccepted, isDeclined]);

  const showActions = !isAccepted && !isDeclined;

  const expandedContent = (
    <>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Mapped to
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {label}
        </Typography>
      </Box>

      {commentary && commentary.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Why this suggestion?
          </Typography>
          <Box component="ul" sx={commentaryListSx}>
            {commentary.map((note, index) => (
              <Typography
                key={index}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={commentaryItemSx}
              >
                {note}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {confidence != null && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Confidence: {Math.round(confidence * 100)}%
          </Typography>
        </Box>
      )}
    </>
  );

  // eslint-disable-next-line no-nested-ternary
  const containerSx = isAccepted ? acceptedChipContainerSx : isDeclined ? declinedChipContainerSx : chipContainerSx;
  const currentRectStyle = isAccepted ? acceptedRectStyle : rectStyle;
  const currentLabelSx = isDeclined ? declinedLabelTypographySx : labelTypographySx;
  const strokeDasharray = isAccepted ? undefined : '6 4';

  return (
    <AISuggestionPopover
      documentReferenceId={value}
      title={popoverTitle}
      subtitle={popoverSubtitle}
      expandedContent={expandedContent}
      acceptLabel="Accept"
      onAccept={showActions && onAccept ? onAccept : undefined}
      onDecline={showActions && onDecline ? onDecline : undefined}
    >
      {({ onMouseEnter, onMouseLeave, ariaDescribedBy }) => (
        <Box
          aria-describedby={ariaDescribedBy}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sx={containerSx}
        >
          <svg style={svgStyle}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                {AI_GRADIENT_STOPS.map(stop => (
                  <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
                ))}
              </linearGradient>
            </defs>
            <rect
              x="1"
              y="1"
              rx="12"
              ry="12"
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
              style={currentRectStyle}
            />
          </svg>
          <Typography variant="body2" sx={currentLabelSx}>
            {label}
          </Typography>
        </Box>
      )}
    </AISuggestionPopover>
  );
};

export default AISuggestionChip;
